import * as THREE from 'three';
import { PARTICLES, ROOM } from '../config';

/**
 * 떠다니는 빛 입자. 천천히 상승하다 천장에 닿으면 바닥 근처로 리셋된다.
 * 색 모드 두 가지:
 *  - 단색(setColor): 챕터 — 황금 ↔ 그 명화의 별빛으로 보간 (ShowDirector가 계산).
 *  - 다색(enableMulticolor): 피날레 — 6 명화 팔레트를 입자별로 분배.
 * setFade로 피날레 후 함께 암전된다. Additive 블렌딩 + depthWrite off.
 */
export class DustField {
  readonly points: THREE.Points;
  private readonly positions: Float32Array;
  private readonly positionAttribute: THREE.BufferAttribute;
  private readonly colors: Float32Array;
  private readonly colorAttribute: THREE.BufferAttribute;
  private readonly velocities: Float32Array;
  private readonly material: THREE.PointsMaterial;

  constructor() {
    const { count, spawnMargin, minVel, velRange } = PARTICLES;
    this.positions = new Float32Array(count * 3);
    this.colors = new Float32Array(count * 3);
    this.velocities = new Float32Array(count);

    for (let i = 0; i < count; i++) {
      this.positions[i * 3] = (Math.random() - 0.5) * (ROOM.w - spawnMargin);
      this.positions[i * 3 + 1] = Math.random() * ROOM.h;
      this.positions[i * 3 + 2] = (Math.random() - 0.5) * (ROOM.d - spawnMargin);
      this.velocities[i] = minVel + Math.random() * velRange;
      this.colors[i * 3] = 1;
      this.colors[i * 3 + 1] = 1;
      this.colors[i * 3 + 2] = 1;
    }

    const geometry = new THREE.BufferGeometry();
    this.positionAttribute = new THREE.BufferAttribute(this.positions, 3);
    this.colorAttribute = new THREE.BufferAttribute(this.colors, 3);
    geometry.setAttribute('position', this.positionAttribute);
    geometry.setAttribute('color', this.colorAttribute);

    this.material = new THREE.PointsMaterial({
      color: PARTICLES.color,
      size: PARTICLES.size,
      transparent: true,
      opacity: PARTICLES.opacity,
      blending: THREE.AdditiveBlending,
      depthWrite: false,
      vertexColors: false, // 기본은 단색(material.color)
    });

    this.points = new THREE.Points(geometry, this.material);
  }

  /** 단색 모드: 모든 입자를 한 색으로 (챕터). */
  setColor(color: THREE.Color): void {
    this.material.color.copy(color);
  }

  /** 다색 모드: 팔레트 색을 입자별로 분배 (피날레). */
  enableMulticolor(palette: THREE.Color[]): void {
    if (palette.length > 0) {
      const { count } = PARTICLES;
      for (let i = 0; i < count; i++) {
        const c = palette[i % palette.length];
        this.colors[i * 3] = c.r;
        this.colors[i * 3 + 1] = c.g;
        this.colors[i * 3 + 2] = c.b;
      }
      this.colorAttribute.needsUpdate = true;
    }
    this.material.color.set(0xffffff); // vertexColors는 material.color와 곱해짐 → 흰색
    this.material.vertexColors = true;
    this.material.needsUpdate = true;
  }

  /** 단색 모드로 복귀 (루프 재시작). */
  disableMulticolor(): void {
    this.material.vertexColors = false;
    this.material.needsUpdate = true;
  }

  /** 1 = 정상, 0 = 사라짐 (암전). */
  setFade(value: number): void {
    this.material.opacity = PARTICLES.opacity * value;
  }

  update(dt: number): void {
    const { count, resetY } = PARTICLES;
    for (let i = 0; i < count; i++) {
      const y = (this.positions[i * 3 + 1] += this.velocities[i] * dt);
      if (y > ROOM.h) this.positions[i * 3 + 1] = resetY;
    }
    this.positionAttribute.needsUpdate = true;
  }
}
